import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { ArrowLeft, ArrowRight, KeyRound, Lock } from 'lucide-react'

import { Button } from '@/components/ui'
import { appRoutes } from '@/constants'
import { getApiErrorMessage } from '@/lib/api-client'
import { useResetPassword } from '../hooks'
import { resetPasswordSchema, type ResetPasswordValues } from '../schemas'
import { AuthLayout } from './auth-layout'
import { AuthField } from './auth-field'
import { zodResolver } from './zod-resolver'

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const [submitting, setSubmitting] = useState(false)
  const mutation = useResetPassword()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { token, password: '', confirmPassword: '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true)
    try {
      await mutation.mutateAsync(values)
      toast.success('Password reset', { description: 'Sign in with your new password.' })
      navigate(appRoutes.auth.login, { replace: true })
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to reset password'))
    } finally {
      setSubmitting(false)
    }
  })

  return (
    <AuthLayout
      title="Choose a new password"
      description="Your password must be at least 8 characters long."
      footer={
        <Link
          to={appRoutes.auth.login}
          className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline"
        >
          <ArrowLeft className="size-4" /> Back to sign in
        </Link>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <AuthField
          label="New Password"
          icon={<Lock />}
          placeholder="At least 8 characters"
          type="password"
          autoComplete="new-password"
          error={errors.password}
          {...register('password')}
        />

        <AuthField
          label="Confirm New Password"
          icon={<KeyRound />}
          placeholder="Re-enter your password"
          type="password"
          autoComplete="new-password"
          error={errors.confirmPassword}
          {...register('confirmPassword')}
        />

        <Button type="submit" size="lg" className="w-full" disabled={submitting}>
          {submitting ? 'Resetting...' : 'Reset Password'}
          {!submitting ? <ArrowRight className="size-4" /> : null}
        </Button>
      </form>
    </AuthLayout>
  )
}